import {
  calculateTaxOnIncome,
  STANDARD_DEDUCTIONS,
  FICA_CONSTANTS,
  STATE_TAX_DATA,
} from '../calculations'

interface FreelancerRateInput {
  targetW2Salary: number
  weeksPTO: number
  billableHoursPerWeek: number
  healthInsuranceCostPerMonth: number
  filingStatus: string
  stateCode: string
}

export function calculateFreelancerRate(input: FreelancerRateInput) {
  const {
    targetW2Salary,
    weeksPTO,
    billableHoursPerWeek,
    healthInsuranceCostPerMonth,
    filingStatus,
    stateCode,
  } = input

  // W-2 employee breakdown
  const w2StandardDeduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single
  const w2TaxableIncome = Math.max(0, targetW2Salary - w2StandardDeduction)
  const w2FederalTax = calculateTaxOnIncome(w2TaxableIncome, filingStatus)
  const w2SS = Math.min(targetW2Salary, FICA_CONSTANTS.ssWageBase) * FICA_CONSTANTS.ssRate
  const w2Medicare = targetW2Salary * FICA_CONSTANTS.medicareRate
  const w2AdditionalMedicare = targetW2Salary > FICA_CONSTANTS.additionalMedicareThreshold
    ? (targetW2Salary - FICA_CONSTANTS.additionalMedicareThreshold) * FICA_CONSTANTS.additionalMedicareRate
    : 0

  const stateEntry = STATE_TAX_DATA.find(s => s.code === stateCode)
  const stateRate = (stateEntry?.rate || 0) / 100
  const w2StateTax = w2TaxableIncome * stateRate

  const w2TotalTax = w2FederalTax + w2SS + w2Medicare + w2AdditionalMedicare + w2StateTax
  const w2TakeHome = targetW2Salary - w2TotalTax

  // Freelancer calculations
  // Working weeks = 52 - PTO - ~10 holidays (assume 2 weeks holidays)
  const holidayWeeks = 2
  const workingWeeks = Math.max(1, 52 - weeksPTO - holidayWeeks)
  const totalBillableHours = workingWeeks * billableHoursPerWeek

  // Annual health insurance cost
  const annualHealthInsurance = healthInsuranceCostPerMonth * 12

  // SE tax: 15.3% on 92.35% of net SE income (both halves)
  // We need to find the gross that, after all taxes and expenses, matches W-2 take-home
  // Use iterative approach for accuracy

  // Start with a rough estimate
  let annualGrossNeeded = targetW2Salary * 1.4 // initial guess

  for (let i = 0; i < 20; i++) {
    const netSEIncome = annualGrossNeeded // assuming minimal business expenses for rate calc
    const seTaxableIncome = netSEIncome * 0.9235

    // SE tax
    const ssSETax = Math.min(seTaxableIncome, FICA_CONSTANTS.ssWageBase) * FICA_CONSTANTS.ssRate * 2
    const medicareSETax = seTaxableIncome * FICA_CONSTANTS.medicareRate * 2
    const additionalMedicareSE = netSEIncome > FICA_CONSTANTS.additionalMedicareThreshold
      ? (seTaxableIncome - FICA_CONSTANTS.additionalMedicareThreshold) * FICA_CONSTANTS.additionalMedicareRate
      : 0
    const totalSETax = ssSETax + medicareSETax + additionalMedicareSE

    const seDeduction = totalSETax / 2
    const standardDeduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single
    const taxableIncome = Math.max(0, netSEIncome - standardDeduction - seDeduction)
    const federalTax = calculateTaxOnIncome(taxableIncome, filingStatus)
    const stateTax = taxableIncome * stateRate

    const totalTax = totalSETax + federalTax + stateTax
    const takeHome = annualGrossNeeded - totalTax - annualHealthInsurance

    // Adjust to match W-2 take-home
    const diff = w2TakeHome - takeHome
    annualGrossNeeded += diff * 0.8 // dampen to converge
  }

  annualGrossNeeded = Math.max(0, annualGrossNeeded)

  // Final tax calculation for the converged amount
  const finalSETaxableIncome = annualGrossNeeded * 0.9235
  const finalSSSETax = Math.min(finalSETaxableIncome, FICA_CONSTANTS.ssWageBase) * FICA_CONSTANTS.ssRate * 2
  const finalMedicareSETax = finalSETaxableIncome * FICA_CONSTANTS.medicareRate * 2
  const finalTotalSETax = finalSSSETax + finalMedicareSETax
  const finalSeDeduction = finalTotalSETax / 2
  const finalStdDeduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single
  const finalTaxableIncome = Math.max(0, annualGrossNeeded - finalStdDeduction - finalSeDeduction)
  const finalFederalTax = calculateTaxOnIncome(finalTaxableIncome, filingStatus)
  const finalStateTax = finalTaxableIncome * stateRate
  const finalTotalTax = finalTotalSETax + finalFederalTax + finalStateTax

  const hourlyRate = totalBillableHours > 0 ? annualGrossNeeded / totalBillableHours : 0
  const dailyRate = billableHoursPerWeek > 0 ? hourlyRate * 8 : 0

  const seTaxOverhead = Math.round(finalTotalSETax - (w2SS + w2Medicare + w2AdditionalMedicare))

  return {
    // Required rates
    hourlyRate: Math.round(hourlyRate * 100) / 100,
    dailyRate: Math.round(dailyRate * 100) / 100,
    annualGrossNeeded: Math.round(annualGrossNeeded),

    // Overhead breakdown
    seTaxOverhead: Math.round(seTaxOverhead),
    annualHealthInsurance: Math.round(annualHealthInsurance),
    totalSETax: Math.round(finalTotalSETax),

    // Working time
    workingWeeks,
    totalBillableHours,

    // W-2 comparison
    w2TotalTax: Math.round(w2TotalTax),
    w2TakeHome: Math.round(w2TakeHome),
    freelancerTotalTax: Math.round(finalTotalTax),
    freelancerTakeHome: Math.round(annualGrossNeeded - finalTotalTax - annualHealthInsurance),

    // Waterfall
    waterfall: [
      { label: 'Target W-2 Take-Home', amount: Math.round(w2TakeHome) },
      { label: 'SE Tax Premium', amount: Math.round(seTaxOverhead) },
      { label: 'Health Insurance', amount: Math.round(annualHealthInsurance) },
      { label: 'Additional Income Tax', amount: Math.round(Math.max(0, finalFederalTax + finalStateTax - w2FederalTax - w2StateTax)) },
      { label: 'PTO Buffer', amount: Math.round(annualGrossNeeded * (weeksPTO + holidayWeeks) / 52) },
    ],
  }
}
